import type { PersonUsage } from '@/billing/api';
import { formatCopy } from '@/i18n/ns';
import { WorkspaceIcon } from '@/workspace/icons';
import { credits, initials, type Formatters } from './format';
import type { UsageCopy } from './copy';
import { LimitEditor } from './limit-editor';

export interface PersonView {
  person: PersonUsage;
  color: string;
  share: number;
  avg: number;
}

export const roleLabel = (copy: UsageCopy, role: PersonUsage['role']) =>
  role === 'owner' ? copy.owner : role === 'admin' ? copy.admin : copy.memberRole;

export function limitState(person: PersonUsage) {
  const used = credits(person.periodUsedMicros);
  const limit = person.monthlyLimitMicros == null ? null : credits(person.monthlyLimitMicros);
  const pct = limit ? Math.round((used / limit) * 100) : 0;
  return {
    used,
    limit,
    pct,
    warn: limit !== null && pct >= 80,
    paused: limit !== null && used >= limit,
  };
}

export function Avatar({ name, color, size = '' }: { name: string; color: string; size?: string }) {
  return (
    <span
      className={`cu-avatar${size ? ` is-${size}` : ''}`}
      style={{ boxShadow: `0 0 0 2px var(--ws-surface), 0 0 0 3.5px ${color}` }}
      aria-hidden="true"
    >
      {initials(name)}
    </span>
  );
}

function UsageCell({ person, copy, f }: { person: PersonUsage; copy: UsageCopy; f: Formatters }) {
  const { used, limit, pct, warn } = limitState(person);
  if (limit === null)
    return (
      <span className="cu-nolimit">{formatCopy(copy.usedNoLimit, { used: f.int(used) })}</span>
    );
  return (
    <span className="cu-usage-cell">
      <span className="cu-usage-top">
        <span>
          <span className="cu-num">{f.int(used)}</span>{' '}
          <span className="cu-of">{formatCopy(copy.ofLimit, { limit: f.int(limit) })}</span>
        </span>
        {warn && <span className="cu-badge">{f.percent(pct)}</span>}
      </span>
      <span className={`cu-meter${warn ? ' is-warn' : ''}`}>
        <span style={{ width: `${Math.min(100, pct)}%` }} />
      </span>
    </span>
  );
}

interface RowProps {
  view: PersonView;
  you: boolean;
  selected: boolean;
  editing: boolean;
  canManage: boolean;
  copy: UsageCopy;
  f: Formatters;
  onSelect: () => void;
  onEdit: () => void;
  onSave: (credits: number | null) => Promise<boolean>;
  onCancel: () => void;
}

function PersonRow({
  view,
  you,
  selected,
  editing,
  canManage,
  copy,
  f,
  onSelect,
  onEdit,
  onSave,
  onCancel,
}: RowProps) {
  const { person, color, share, avg } = view;
  const { used, limit, paused } = limitState(person);
  return (
    <div
      className={`cu-urow${selected ? ' is-selected' : ''}${editing ? ' is-editing' : ''}`}
      role="rowgroup"
      data-person={person.userId}
    >
      <div className="cu-tr cu-user-grid" role="row" onClick={onSelect}>
        <span role="cell" className="cu-c-member">
          <button
            type="button"
            className="cu-member cu-member-btn"
            aria-pressed={selected}
            aria-label={formatCopy(selected ? copy.selectedShowAll : copy.showSpendFor, {
              name: person.name,
            })}
          >
            <Avatar name={person.name} color={color} />
            <span className="cu-member-text">
              <span className="cu-member-name">
                {person.name}
                {you && <span className="cu-tag">{copy.you}</span>}
                {paused && <span className="cu-tag is-warn">{copy.pausedTag}</span>}
              </span>
              <span className="cu-member-role">{roleLabel(copy, person.role)}</span>
            </span>
          </button>
        </span>
        <span role="cell" className="cu-td-r cu-c-credits">
          {f.int(credits(person.creditMicros))}
          <span className="cu-compact-meta">
            {f.percent(share)} · {f.one(avg)}
          </span>
        </span>
        <span role="cell" className="cu-td-r cu-c-share">
          {f.percent(share)}
        </span>
        <span role="cell" className="cu-td-r cu-c-avg">
          {f.one(avg)}
        </span>
        <span role="cell" className="cu-c-usage">
          <UsageCell person={person} copy={copy} f={f} />
        </span>
        <span role="cell" className="cu-cell-actions cu-c-limit">
          {canManage && (
            <button
              type="button"
              className="cu-btn is-sm is-quiet"
              disabled={editing}
              onClick={(event) => {
                // The row selects the person; the limit button only edits.
                event.stopPropagation();
                onEdit();
              }}
            >
              {limit === null ? copy.setLimit : copy.editLimit}
            </button>
          )}
        </span>
        <span role="cell" className="cu-c-chev" aria-hidden="true">
          <WorkspaceIcon name={selected ? 'check' : 'chevron'} size={16} />
        </span>
      </div>
      {editing && (
        <LimitEditor
          name={person.name}
          usedCredits={used}
          limitCredits={limit}
          copy={copy}
          f={f}
          onSave={onSave}
          onCancel={onCancel}
        />
      )}
    </div>
  );
}

export function PeopleList({
  views,
  currentUserId,
  selectedId,
  editingId,
  canManage,
  copy,
  f,
  onSelect,
  onEdit,
  onSave,
  onCancel,
}: {
  views: PersonView[];
  currentUserId: string | null;
  selectedId: string | null;
  editingId: string | null;
  canManage: boolean;
  copy: UsageCopy;
  f: Formatters;
  onSelect: (userId: string) => void;
  onEdit: (userId: string) => void;
  onSave: (person: PersonUsage, credits: number | null) => Promise<boolean>;
  onCancel: () => void;
}) {
  return (
    <div className="cu-users" role="table" aria-label={copy.userTitle}>
      <div className="cu-tr is-head cu-user-grid" role="row">
        <span role="columnheader">{copy.member}</span>
        <span role="columnheader" className="cu-td-r">
          {copy.credits}
        </span>
        <span role="columnheader" className="cu-td-r">
          {copy.share}
        </span>
        <span role="columnheader" className="cu-td-r">
          {copy.avgPerDay}
        </span>
        <span role="columnheader">{copy.usedLimit}</span>
        <span role="columnheader">
          <span className="cu-sr">{copy.limitColumn}</span>
        </span>
        <span role="columnheader">
          <span className="cu-sr">{copy.selectedColumn}</span>
        </span>
      </div>
      {views.map((view) => (
        <PersonRow
          key={view.person.userId}
          view={view}
          you={view.person.userId === currentUserId}
          selected={view.person.userId === selectedId}
          editing={view.person.userId === editingId}
          canManage={canManage}
          copy={copy}
          f={f}
          onSelect={() => onSelect(view.person.userId)}
          onEdit={() => onEdit(view.person.userId)}
          onSave={(value) => onSave(view.person, value)}
          onCancel={onCancel}
        />
      ))}
    </div>
  );
}
